import React, {
  ReactElement,
  ElementType,
  ReactNode,
  MouseEventHandler
} from 'react'
import { Chip as MUIChip, SxProps } from '@mui/material'
import { Color } from 'themes'

import LinkWrapper from '../LinkWrapper'

export interface ChipProps {
  avatar?: ReactElement
  clickable?: boolean
  color?:
    | Color
    | 'default'
    | 'error'
    | 'info'
    | 'success'
    | 'warning'
    | 'brand'
    | 'black'
    | 'white'
    | 'transparent'
  component?: ElementType<any>
  deleteIcon?: ReactElement
  disabled?: boolean
  href?: string
  icon?: ReactElement
  label?: ReactNode
  newTab?: boolean // open href in new tab
  onClick?: (() => void) | MouseEventHandler<HTMLElement>
  onDelete?: (() => void) | MouseEventHandler<HTMLElement>
  onMouseDown?: (() => void) | MouseEventHandler<HTMLElement>
  search?: string
  size?: 'medium' | 'small' | 'xsmall'
  sx?: SxProps
  variant?: 'filled' | 'outlined'
}

/**
 * The Chip component is a compact element
 * that represent an input, attribute, or action.
 */

const Chip = React.forwardRef((props: ChipProps, ref: any) => {
  const { color, disabled, href, newTab, search, size, sx, ...rest } = props

  return (
    <LinkWrapper
      disabled={disabled}
      href={href}
      newTab={newTab}
      search={search}
      style={{ minWidth: 0 }}
    >
      <MUIChip
        color={color !== 'transparent' ? color : undefined}
        disabled={disabled}
        size={size === 'xsmall' ? 'small' : size}
        sx={{
          '& .MuiChip-label': {
            bottom:
              size === 'xsmall'
                ? '-0.12em'
                : size === 'small'
                  ? '-0.06em'
                  : undefined,
            position: 'relative'
          },
          ...(color
            ? {
                bgcolor: color === 'transparent' ? 'transparent' : undefined
              }
            : null),
          ...(size === 'xsmall'
            ? {
                fontSize: '0.7rem',
                height: '18px',
                '& .MuiChip-icon': { fontSize: 14 }
              }
            : null),
          ...sx
        }}
        {...rest}
        ref={ref}
      />
    </LinkWrapper>
  )
})

Chip.displayName = 'Chip'
export default Chip
